/**
 * Category-Specific FundingGauge Configurations
 *
 * Each award category has different:
 * - Financial fields available
 * - Progress calculation method
 * - Metrics to display
 * - Labels and terminology
 *
 * See docs/usaspending-funding-progress-by-category.md for documentation.
 */
import type { FundingGaugeCategory, FundingGaugeConfig } from './types'

// ============================================================================
// Category Configurations
// ============================================================================

/**
 * Contract: Full obligation lifecycle with options.
 * Progress = obligated / potential (base + all options)
 */
const contractConfig: FundingGaugeConfig = {
  category: 'contract',
  title: 'Funding Progress',
  hasProgressBar: true,
  progressNumerator: 'obligatedAmount',
  progressDenominator: 'potentialAwardAmount',
  progressLabel: 'Obligated',
  metricIds: ['obligated', 'current', 'potential', 'outlayed'],
}

/**
 * IDV (Indefinite Delivery Vehicle): Master contract umbrella.
 * NO progress bar - obligations happen at child order level.
 * Shows ceiling values and note about child orders.
 */
const idvConfig: FundingGaugeConfig = {
  category: 'idv',
  title: 'Contract Ceiling',
  hasProgressBar: false,
  progressLabel: '',
  note: 'Obligations and outlays are tracked at the individual order level, not the IDV level.',
  metricIds: ['ceiling', 'currentValue', 'childObligated', 'childOutlayed'],
}

/**
 * Grant: Fixed award amount, track disbursement.
 * Progress = outlayed / obligated (disbursement rate)
 * No "potential" concept - grants have fixed amounts.
 */
const grantConfig: FundingGaugeConfig = {
  category: 'grant',
  title: 'Disbursement Progress',
  hasProgressBar: true,
  progressNumerator: 'totalOutlayedAmount',
  progressDenominator: 'obligatedAmount',
  progressLabel: 'Disbursed',
  metricIds: ['obligated', 'award', 'outlayed', 'nonFederal'],
}

/**
 * Direct Payment: Simple obligation to disbursement flow.
 * Progress = outlayed / obligated
 * No potential/options concept.
 */
const directPaymentConfig: FundingGaugeConfig = {
  category: 'direct_payment',
  title: 'Disbursement Progress',
  hasProgressBar: true,
  progressNumerator: 'totalOutlayedAmount',
  progressDenominator: 'obligatedAmount',
  progressLabel: 'Disbursed',
  metricIds: ['authorized', 'obligated_dp', 'disbursed'],
}

/**
 * Direct Loan (Type 07): Federal direct loans.
 * Progress = outlayed / loan_value (loan disbursement rate)
 * Uses loan-specific terminology.
 * Note: total_obligation = 0 for loans.
 */
const directLoanConfig: FundingGaugeConfig = {
  category: 'direct_loan',
  title: 'Loan Disbursement Progress',
  hasProgressBar: true,
  progressNumerator: 'totalOutlayedAmount',
  progressDenominator: 'totalLoanValue',
  progressLabel: 'Disbursed',
  metricIds: ['loanValue', 'subsidyCost', 'loanDisbursed'],
}

/**
 * Guaranteed Loan (Type 08): Federally guaranteed loans.
 * NO progress bar - no federal disbursement (private lender disburses).
 * Shows loan value and guaranteed amount only.
 */
const guaranteedLoanConfig: FundingGaugeConfig = {
  category: 'guaranteed_loan',
  title: 'Loan Guarantee',
  hasProgressBar: false,
  progressLabel: '',
  note: 'No federal disbursement for guaranteed loans. The private lender disburses funds directly.',
  metricIds: ['loanValue', 'guaranteedAmount'],
}

/**
 * Insurance (Type 09): Federal insurance programs.
 * NO progress bar - limited meaningful progress tracking.
 */
const insuranceConfig: FundingGaugeConfig = {
  category: 'insurance',
  title: 'Insurance Coverage',
  hasProgressBar: false,
  progressLabel: '',
  note: 'Insurance programs do not have traditional funding progress metrics.',
  metricIds: ['coverageAmount'],
}

// ============================================================================
// Configuration Map
// ============================================================================

const configMap: Record<FundingGaugeCategory, FundingGaugeConfig> = {
  contract: contractConfig,
  idv: idvConfig,
  grant: grantConfig,
  direct_payment: directPaymentConfig,
  direct_loan: directLoanConfig,
  guaranteed_loan: guaranteedLoanConfig,
  insurance: insuranceConfig,
}

/**
 * Get the funding gauge configuration for a category.
 */
export function getCategoryFundingConfig(
  category: FundingGaugeCategory,
): FundingGaugeConfig {
  return configMap[category]
}

// ============================================================================
// Category Detection
// ============================================================================

/**
 * Normalize raw category string to FundingGaugeCategory.
 * Handles various API formats and edge cases.
 *
 * @param category - Raw category string from API (e.g., "contract", "Grant", "loans")
 * @param type - Optional award type code (e.g., "07", "08") for loan disambiguation
 */
export function normalizeCategoryForFunding(
  category: string | null | undefined,
  type?: string | null,
): FundingGaugeCategory {
  if (!category) return 'contract' // Default fallback

  const normalized = category.toLowerCase().trim()

  // Handle loan type disambiguation
  if (normalized === 'loans' || normalized === 'loan') {
    // Type 07 = Direct Loan, Type 08 = Guaranteed Loan
    if (type === '07') return 'direct_loan'
    if (type === '08') return 'guaranteed_loan'
    // Default to direct loan if type not specified
    return 'direct_loan'
  }

  // Map category strings to enum values
  switch (normalized) {
    case 'contract':
      return 'contract'
    case 'idv':
      return 'idv'
    case 'grant':
      return 'grant'
    case 'direct payment':
    case 'direct_payment':
    case 'other':
      return 'direct_payment'
    case 'insurance':
      return 'insurance'
    default:
      return 'contract' // Fallback for unknown categories
  }
}

export { configMap }
